import React, { useRef, useState, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import cn from 'classnames'
import styles from '../upvalue.module.scss'
import { SignalButton } from './SignalButton'
import { SignalOptionsButton } from './SignalOptionsButton'
import { SignalOptions } from '../SignalOptions'
import { SignalType } from 'types/signals'
import { Answer, IReaction } from 'types/answers'
import type { Quest } from 'types/quests'
import useStore from 'state/knovStore'
import api from 'api/api'
import { SATOSHI_UNIT, SATS_BEFORE_CONFIRM, USD_UNIT, toBitcoin } from 'lib/bsv-util'
import { sameUserId } from 'state/freeReactionEligibility'
import type { ReactOrUpvalueOptions } from 'state/upvalueQueue'
import { DEFAULT_UPVALUE_EMOJI } from '../upvalueEmoji'
import { seedMinimumSats, seedQuickSats, useSeedingContextForAnswer } from 'lib/seeding'
import { hasActiveSeedPosition } from 'lib/seedPosition'
import { quickSignalPressIsFree } from './signalButtonPresentation'
import { updateStreamSeedSelection } from 'lib/seedingStream'
import SimpleUpvalueModal from 'components/modals/SimpleUpvalueModal'

type SeedSelectionWriteState = {
    confirmed: boolean
    tail: Promise<void>
    version: number
}

const seedSelectionWrites = new Map<string, SeedSelectionWriteState>()

async function enqueueSeedSelectionWrite(
    streamId: string,
    seedSelected: boolean,
    initialConfirmed: boolean,
) {
    const writeState =
        seedSelectionWrites.get(streamId) ||
        ({
            confirmed: initialConfirmed,
            tail: Promise.resolve(),
            version: 0,
        } as SeedSelectionWriteState)
    seedSelectionWrites.set(streamId, writeState)

    const version = writeState.version + 1
    writeState.version = version
    let error: unknown = null
    writeState.tail = writeState.tail.then(async () => {
        try {
            await api.updateTeamSeedSelection(streamId, seedSelected)
            writeState.confirmed = seedSelected
        } catch (caughtError) {
            error = caughtError
        }
    })

    await writeState.tail
    const latest = writeState.version === version
    if (latest) seedSelectionWrites.delete(streamId)

    return { confirmed: writeState.confirmed, error, latest }
}

function seedSelectionFromStore(streamId: string) {
    const store = useStore.getState()
    const stream = [...(store.userStreams || []), ...(store.publicTeams || [])].find(
        item => String(item.id) === String(streamId),
    )
    return stream?.seed_selected === true
}

function setStreamSeedSelection(streamId: string, seedSelected: boolean) {
    useStore.setState(state => ({
        userStreams: updateStreamSeedSelection(state.userStreams || [], streamId, seedSelected),
        publicTeams: updateStreamSeedSelection(state.publicTeams || [], streamId, seedSelected),
    }))
}

interface UpvalueButtonProps {
    signalType: SignalType
    showBoost: boolean
    theme?: 'play'
    onOpenReactionPicker?: () => void
    onCloseReactionPicker?: () => void
    onReactionCommitted?: () => void
    handleLockOrUpvalue: (
        sats: number,
        blocks?: number,
        type?: SignalType,
        options?: ReactOrUpvalueOptions,
    ) => void
    animateLock: (ref: React.RefObject<HTMLDivElement>, scale?: number) => void
    currentUser?: { id: string | number } | null
    answer: Answer
    signalQuest?: Quest
    containerRef?: React.RefObject<HTMLDivElement>
    onEffectTrigger?: (element: HTMLElement | null) => void
    clickPower?: number
    nextPressIsFree?: boolean
    upvaluesEnabled?: boolean
    freeReactionAvailable?: boolean
    useSimpleUpvalueModal?: boolean
    quickOnly?: boolean
}

export function UpvalueButton({
    signalType,
    showBoost,
    theme,
    onOpenReactionPicker,
    onCloseReactionPicker,
    onReactionCommitted,
    handleLockOrUpvalue,
    animateLock,
    currentUser,
    answer,
    signalQuest,
    containerRef,
    onEffectTrigger,
    clickPower = 0,
    nextPressIsFree = false,
    upvaluesEnabled = true,
    freeReactionAvailable = false,
    useSimpleUpvalueModal = false,
    quickOnly = false,
}: UpvalueButtonProps) {
    const quickLockRef = useRef<HTMLDivElement>(null)
    const signalOptionsRef = useRef<HTMLDivElement>(null)
    const inlineSignalOptionsRef = useRef<HTMLDivElement>(null)
    const [inlineSignalOptionsStyle, setInlineSignalOptionsStyle] = useState<
        React.CSSProperties | undefined
    >(undefined)

    // Get state from store
    const userBsvUnit = useStore(state => state.bsvUnit)
    const upvalueSats = useStore(state => state.upvalueSats)
    const upvalueUsd = useStore(state => state.upvalueUsd)
    const seedSats = useStore(state => state.seedSats)
    const seedUsd = useStore(state => state.seedUsd)
    const effectEnabled = useStore(state => state.effectEnabled)
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)
    const useSats = userBsvUnit === SATOSHI_UNIT
    const useUsd = userBsvUnit === USD_UNIT
    const seedingContext = useSeedingContextForAnswer(answer, signalQuest)
    const persistedSeedSelection = seedingContext.forceSeed || seedingContext.seedSelected
    const [selectedSeed, setSelectedSeed] = useState(persistedSeedSelection)
    const seedModeActive = seedingContext.requestedSeed(selectedSeed)
    const minimumSeedSats = seedMinimumSats()
    const quickSignalSats = seedModeActive ? seedQuickSats(seedSats, minimumSeedSats) : upvalueSats
    const quickSignalUsd = seedModeActive
        ? quickSignalSats === seedSats
            ? seedUsd
            : null
        : upvalueUsd
    // Filled button state: the current user already holds a seed position here.
    const alreadySeeded = React.useMemo(
        () => hasActiveSeedPosition(answer.bsv_upvalues, currentUser?.id),
        [answer.bsv_upvalues, currentUser?.id],
    )

    // State for animated values
    const [animatedUpvalueSats, setAnimatedUpvalueSats] = useState<number>(upvalueSats)

    useEffect(() => {
        setSelectedSeed(persistedSeedSelection)
    }, [persistedSeedSelection, seedingContext.streamId])

    // Sync animated values when global state changes
    useEffect(() => {
        setAnimatedUpvalueSats(upvalueSats)
    }, [upvalueSats])

    const userLatestReaction = React.useMemo(() => {
        let latestReaction: string | null = null
        let latestDate = 0

        // Free reactions count alongside paid upvalues for the active emoji.
        if (currentUser) {
            const freeReaction = (answer.reactions || []).find(reaction =>
                sameUserId(reaction.user_id, currentUser.id),
            )
            if (freeReaction) {
                latestReaction = freeReaction.emoji
                latestDate = new Date(
                    freeReaction.updated_at || freeReaction.created_at || 0,
                ).getTime()
            }
        }

        if (!answer.bsv_upvalues) return latestReaction

        for (const upv of answer.bsv_upvalues) {
            const emoji = upv?.metadata?.sentiment_emoji
            const source = upv?.metadata?.sentiment_source
            if (!emoji) continue
            if (source === 'sentiment_breakdown') continue

            if (currentUser && sameUserId(upv.from_user_id, currentUser.id)) {
                const date = new Date(upv.created_at || 0).getTime()
                if (date > latestDate) {
                    latestDate = date
                    latestReaction = emoji
                }
            }
        }

        return latestReaction
    }, [answer.bsv_upvalues, answer.reactions, currentUser?.id])

    const [selectedReaction, setSelectedReaction] = useState<string | undefined>(
        userLatestReaction ?? undefined,
    )
    const [isInlineSignalOptionsOpen, setIsInlineSignalOptionsOpen] = useState(false)
    const [isSimpleUpvalueModalOpen, setIsSimpleUpvalueModalOpen] = useState(false)
    const [localCommittedReaction, setLocalCommittedReaction] = useState<{
        answerId: string
        userId: string | number
        emoji: string
        createdAt: string
    } | null>(null)

    const currentUserReaction = React.useMemo(() => {
        if (!currentUser) return undefined
        return (answer.reactions || []).find(reaction =>
            sameUserId(reaction.user_id, currentUser.id),
        )
    }, [answer.reactions, currentUser?.id])

    const localCommittedReactionForAnswer =
        localCommittedReaction &&
        currentUser &&
        localCommittedReaction.answerId === answer.id &&
        sameUserId(localCommittedReaction.userId, currentUser.id)
            ? localCommittedReaction
            : null

    const hasLocalFreeReaction = Boolean(localCommittedReactionForAnswer) && !currentUserReaction
    const effectiveNextPressIsFree = nextPressIsFree && !hasLocalFreeReaction
    const quickPressIsFree = quickSignalPressIsFree(effectiveNextPressIsFree, seedModeActive)
    const effectiveFreeReactionAvailable = hasLocalFreeReaction
        ? !upvaluesEnabled
        : freeReactionAvailable
    const effectiveSelectedReaction =
        selectedReaction ||
        localCommittedReactionForAnswer?.emoji ||
        userLatestReaction ||
        undefined

    const effectiveAnswer = React.useMemo(() => {
        if (!localCommittedReactionForAnswer || currentUserReaction) return answer

        const syntheticReaction: IReaction = {
            id: `local-reaction-${answer.id}-${localCommittedReactionForAnswer.userId}`,
            answer_id: answer.id,
            user_id: String(localCommittedReactionForAnswer.userId),
            emoji: localCommittedReactionForAnswer.emoji,
            created_at: localCommittedReactionForAnswer.createdAt,
            updated_at: localCommittedReactionForAnswer.createdAt,
            user_name: gon.currentUser?.name,
            is_synthetic: true,
        }

        return {
            ...answer,
            reactions: [
                ...(answer.reactions || []).filter(
                    reaction =>
                        !sameUserId(reaction.user_id, localCommittedReactionForAnswer.userId),
                ),
                syntheticReaction,
            ],
        }
    }, [answer, currentUserReaction, localCommittedReactionForAnswer])

    const hasSignalAction = upvaluesEnabled || effectiveFreeReactionAvailable
    const simpleUpvalueModalAvailable = useSimpleUpvalueModal && upvaluesEnabled

    React.useEffect(() => {
        setLocalCommittedReaction(null)
        setSelectedReaction(userLatestReaction ?? undefined)
    }, [answer.id, currentUser?.id])

    React.useEffect(() => {
        // Only hydrate from history when nothing is selected yet.
        // Prevent sentiment-breakdown upvalues from overriding the active button emoji.
        if (!selectedReaction && userLatestReaction) {
            setSelectedReaction(userLatestReaction)
        }
    }, [selectedReaction, userLatestReaction])

    const handleReactionSelected = React.useCallback((reaction: string) => {
        setSelectedReaction(reaction)
    }, [])

    const handleSeedChange = useCallback(
        (seedSelected: boolean) => {
            setSelectedSeed(seedSelected)

            const streamId = seedingContext.streamId
            if (!seedingContext.canChooseSeed || !streamId) return

            const confirmedSelection = seedSelectionFromStore(streamId)
            setStreamSeedSelection(streamId, seedSelected)

            void enqueueSeedSelectionWrite(streamId, seedSelected, confirmedSelection).then(
                result => {
                    if (!result.error || !result.latest) return

                    setStreamSeedSelection(streamId, result.confirmed)
                    setSelectedSeed(result.confirmed)
                    useStore
                        .getState()
                        .showGlobalNotice('Unable to save the channel Seed setting.', {
                            variant: 'error',
                        })
                },
            )
        },
        [seedingContext.canChooseSeed, seedingContext.streamId],
    )

    const handleSignalAction = React.useCallback(
        (sats: number, blocks?: number, type?: SignalType, options?: ReactOrUpvalueOptions) => {
            const fromSignalOptions = options?.source === 'signal_options'
            const normalizedSats = Math.round(sats || 0)
            const sendsFreeReaction =
                (fromSignalOptions && normalizedSats < 1 && effectiveFreeReactionAvailable) ||
                (!fromSignalOptions &&
                    quickSignalPressIsFree(effectiveNextPressIsFree, options?.isSeed === true))

            if (!sendsFreeReaction || !currentUser?.id) {
                handleLockOrUpvalue(sats, blocks, type, options)
                return
            }

            const emoji = options?.reactionEmoji || DEFAULT_UPVALUE_EMOJI
            const reactionState = {
                answerId: answer.id,
                userId: currentUser.id,
                emoji,
                createdAt: new Date().toISOString(),
            }
            setLocalCommittedReaction(reactionState)
            setSelectedReaction(emoji)

            handleLockOrUpvalue(sats, blocks, type, {
                ...options,
                onFreeReactionCommitted: reaction => {
                    options?.onFreeReactionCommitted?.(reaction)
                },
                onFreeReactionFailed: error => {
                    setLocalCommittedReaction(currentReaction => {
                        if (
                            currentReaction?.answerId === reactionState.answerId &&
                            sameUserId(currentReaction.userId, reactionState.userId)
                        ) {
                            return null
                        }

                        return currentReaction
                    })
                    options?.onFreeReactionFailed?.(error)
                },
            })
        },
        [
            answer.id,
            currentUser?.id,
            effectiveFreeReactionAvailable,
            effectiveNextPressIsFree,
            handleLockOrUpvalue,
        ],
    )

    const closeInlineSignalOptions = useCallback(
        (resumePlayback = true) => {
            setIsInlineSignalOptionsOpen(false)
            if (resumePlayback) {
                onCloseReactionPicker?.()
            }
        },
        [onCloseReactionPicker],
    )

    const closeSimpleUpvalueModal = useCallback(() => {
        setIsSimpleUpvalueModalOpen(false)
        onCloseReactionPicker?.()
    }, [onCloseReactionPicker])

    const handleOpenSignalOptions = useCallback(() => {
        if (!hasSignalAction) return

        if (!gon?.currentUser) {
            openAuthModal('signup', 'upvalue_signal_options')
            return
        }

        if (simpleUpvalueModalAvailable) {
            setIsSimpleUpvalueModalOpen(currentValue => {
                const nextValue = !currentValue
                if (nextValue) {
                    onOpenReactionPicker?.()
                } else {
                    onCloseReactionPicker?.()
                }
                return nextValue
            })
            return
        }

        if (theme === 'play') {
            setIsInlineSignalOptionsOpen(currentValue => {
                const nextValue = !currentValue
                if (nextValue) {
                    onOpenReactionPicker?.()
                } else {
                    onCloseReactionPicker?.()
                }
                return nextValue
            })
            return
        }
        useStore.setState({
            isModalOpen: true,
            modalParams: {
                type: 'SignalOptions',
                noHeader: false,
                icon: 'SmilePlus',
                showBoost,
                customUpvalueSats: animatedUpvalueSats,
                customUpvalueUsd: upvalueUsd,
                quickLockRef: { current: containerRef?.current ?? null },
                handleLockOrUpvalue: handleSignalAction,
                animateLock,
                answer: effectiveAnswer,
                signalQuest,
                selectedReaction: effectiveSelectedReaction,
                onReactionSelected: handleReactionSelected,
                onReactionCommitted,
                initialSeed: seedModeActive,
                onSeedChange: handleSeedChange,
                customStyles: {
                    width: '38vw',
                    minWidth: '440px',
                    maxWidth: '640px',
                    maxHeight: 'calc(100dvh - 80px)',
                    left: '50%',
                    transform: 'translate(-50%, 0%)',
                },
                mobileCustomStyles: {
                    width: '100dvw',
                    maxWidth: '100dvw',
                    minWidth: '0',
                    maxHeight: '100dvh',
                    height: '100dvh',
                    left: '0',
                    transform: 'none',
                    top: '0',
                },
            },
        })
    }, [
        animatedUpvalueSats,
        answer,
        animateLock,
        containerRef,
        effectiveAnswer,
        effectiveSelectedReaction,
        handleSignalAction,
        handleSeedChange,
        handleReactionSelected,
        hasSignalAction,
        openAuthModal,
        onReactionCommitted,
        selectedReaction,
        signalQuest,
        seedModeActive,
        showBoost,
        theme,
        upvalueUsd,
        onOpenReactionPicker,
        onCloseReactionPicker,
        simpleUpvalueModalAvailable,
    ])

    useEffect(() => {
        if (!isInlineSignalOptionsOpen) return

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeInlineSignalOptions()
            }
        }

        document.addEventListener('keydown', handleEscape)

        return () => {
            document.removeEventListener('keydown', handleEscape)
        }
    }, [closeInlineSignalOptions, isInlineSignalOptionsOpen])

    useEffect(() => {
        if (theme !== 'play' || !isInlineSignalOptionsOpen) {
            setInlineSignalOptionsStyle(undefined)
            return
        }

        const updateInlineSignalOptionsPosition = () => {
            const anchorRect =
                containerRef?.current?.getBoundingClientRect() ||
                signalOptionsRef.current?.getBoundingClientRect()

            if (!anchorRect || window.innerWidth <= 640) {
                setInlineSignalOptionsStyle(undefined)
                return
            }

            const rightOffset = Math.max(12, window.innerWidth - anchorRect.right)
            setInlineSignalOptionsStyle({
                left: 'auto',
                right: `${rightOffset}px`,
                transform: 'none',
            })
        }

        updateInlineSignalOptionsPosition()
        window.addEventListener('resize', updateInlineSignalOptionsPosition)

        return () => {
            window.removeEventListener('resize', updateInlineSignalOptionsPosition)
        }
    }, [containerRef, isInlineSignalOptionsOpen, theme])

    const inlineSignalOptionsLayer =
        theme === 'play' && isInlineSignalOptionsOpen && typeof document !== 'undefined'
            ? ReactDOM.createPortal(
                  <>
                      <div
                          className={styles.inlineSignalOptionsBackdrop}
                          data-no-modal-interaction
                          data-story-control-exempt="true"
                          onClick={event => {
                              event.stopPropagation()
                              event.preventDefault()
                              closeInlineSignalOptions()
                          }}
                          onMouseDown={event => {
                              event.stopPropagation()
                              event.preventDefault()
                          }}
                          onPointerDown={event => {
                              event.stopPropagation()
                              event.preventDefault()
                          }}
                          onTouchStart={event => {
                              event.stopPropagation()
                              event.preventDefault()
                          }}
                      />
                      <div
                          ref={inlineSignalOptionsRef}
                          className={styles.inlineSignalOptionsPopup}
                          style={inlineSignalOptionsStyle}
                          data-no-modal-interaction
                          data-story-control-exempt="true"
                          onClick={event => event.stopPropagation()}
                          onMouseDown={event => event.stopPropagation()}
                          onPointerDown={event => event.stopPropagation()}
                          onTouchStart={event => event.stopPropagation()}
                      >
                          <SignalOptions
                              showBoost={showBoost}
                              customUpvalueSats={animatedUpvalueSats}
                              customUpvalueUsd={upvalueUsd}
                              quickLockRef={{ current: containerRef?.current ?? null }}
                              handleLockOrUpvalue={handleSignalAction}
                              animateLock={animateLock}
                              answer={effectiveAnswer}
                              signalQuest={signalQuest}
                              selectedReaction={effectiveSelectedReaction}
                              onReactionSelected={handleReactionSelected}
                              onReactionCommitted={onReactionCommitted}
                              initialSeed={seedModeActive}
                              onSeedChange={handleSeedChange}
                              closeModal={() => closeInlineSignalOptions(false)}
                              closeInlinePicker={closeInlineSignalOptions}
                              variant="inline"
                          />
                      </div>
                  </>,
                  document.body,
              )
            : null

    return (
        <div className={styles.upvalueButtonOuterGroup}>
            <div
                ref={containerRef}
                className={cn(styles.signalButtonContainer, clickPower > 0 && styles.containerGlow)}
                style={
                    {
                        '--click-power': clickPower,
                    } as React.CSSProperties
                }
            >
                <SignalButton
                    signalType={signalType}
                    showBoost={showBoost}
                    useSats={useSats}
                    useUsd={useUsd}
                    quickLockRef={quickLockRef}
                    upvalueSats={quickSignalSats}
                    upvalueUsd={quickSignalUsd}
                    handleLockOrUpvalue={handleSignalAction}
                    animateLock={animateLock}
                    currentUser={currentUser}
                    answer={answer}
                    containerRef={containerRef}
                    onEffectTrigger={onEffectTrigger}
                    clickPower={effectEnabled ? clickPower : 0}
                    hasLeadingControls={false}
                    activeReaction={effectiveSelectedReaction}
                    nextPressIsFree={quickPressIsFree}
                    upvaluesEnabled={upvaluesEnabled}
                    freeReactionAvailable={effectiveFreeReactionAvailable}
                    seed={seedModeActive}
                    alreadySeeded={alreadySeeded}
                    minimumSeedSats={minimumSeedSats}
                    onOpenSignalOptions={handleOpenSignalOptions}
                />
                {hasSignalAction && !quickOnly && (
                    <SignalOptionsButton
                        signalType={signalType}
                        signalOptionsRef={signalOptionsRef}
                        onToggleModal={handleOpenSignalOptions}
                        seed={seedModeActive}
                    />
                )}
            </div>
            {inlineSignalOptionsLayer}
            {simpleUpvalueModalAvailable && isSimpleUpvalueModalOpen && (
                <SimpleUpvalueModal
                    answer={effectiveAnswer}
                    signalQuest={signalQuest}
                    open
                    onClose={closeSimpleUpvalueModal}
                    selectedReaction={effectiveSelectedReaction}
                    onReactionSelected={handleReactionSelected}
                    onReactionCommitted={onReactionCommitted}
                />
            )}
        </div>
    )
}
